import { useRef, useState, type JSX } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
  TriangleAlert,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SafeMarkdown } from "@/pages/platform-analytics/SafeMarkdown";
import {
  askAnalytics,
  getAnalyticsBrief,
  getAnalyticsChatStatus,
  refreshAnalyticsBrief,
  type AnalyticsChatMessage,
  type AnalyticsChatStep,
} from "@/services/analyticsChatService";

/**
 * Ask questions of the product-analytics data.
 *
 * Deliberately a conversation and not a dashboard: the analytics spec ruled out
 * charts on the grounds that we do not yet know which questions repeat, and a
 * chat discovers them where a fixed layout has to guess. When a question gets
 * asked often enough, it graduates to canonical-queries.sql.
 */

/** Openers that show what the data can answer, so the page is not a blank box. */
const SUGGESTIONS = [
  "Which tenants have not used the product in the last 30 days?",
  "What is failing? Show me API errors by route this week.",
  "Which screens does nobody visit?",
  "Where do people drop out of onboarding?",
];

interface Turn {
  question: string;
  answer?: string;
  steps?: AnalyticsChatStep[];
  failed?: boolean;
}

function QueryDisclosure({ steps }: { steps: AnalyticsChatStep[] }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (steps.length === 0) return null;
  return (
    <div className="mt-3 border-t border-[var(--fx-ink-200)] pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-[var(--fx-ink-500)] hover:text-[var(--fx-ink-900)]"
      >
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
        {steps.length === 1 ? "1 query" : `${steps.length} queries`}
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          {steps.map((step, i) => (
            <div key={i}>
              <pre className="overflow-x-auto rounded-[var(--fx-r-md)] bg-[var(--fx-s-raised-hover)] p-2 text-[11px] leading-relaxed">
                <code>{step.sql}</code>
              </pre>
              <p className="mt-1 text-[11px] text-[var(--fx-ink-500)]">
                {step.error
                  ? `error: ${step.error}`
                  : `${step.rowCount} row${step.rowCount === 1 ? "" : "s"}${
                      step.truncated ? " (truncated)" : ""
                    }`}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The standing analysis, regenerated daily by a scheduled task.
 *
 * Read from storage rather than generated on mount: a page view must not cost
 * model tokens or seconds of latency, and two admins opening the page must not
 * compute the same answer twice.
 */
function DailyBrief(): JSX.Element | null {
  const qc = useQueryClient();
  const brief = useQuery({
    queryKey: ["platform-analytics", "brief"],
    queryFn: getAnalyticsBrief,
    staleTime: 10 * 60 * 1000,
  });

  const refresh = useMutation({
    mutationFn: refreshAnalyticsBrief,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["platform-analytics", "brief"] });
    },
  });

  if (brief.isLoading || !brief.data) return null;
  const { markdown, generatedAt, error } = brief.data;
  if (markdown === "" && !error) return null;

  return (
    <Card className="mb-6">
      <CardContent className="py-4 text-sm">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs uppercase tracking-wide text-[var(--fx-ink-500)]">
            {generatedAt
              ? `Resumen del ${generatedAt.slice(0, 10)}`
              : "Resumen"}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${refresh.isPending ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
        {error ? (
          // Say the refresh failed rather than presenting stale text as current.
          <p className="text-[var(--fx-warn-text)]">
            La última actualización falló: {error}
          </p>
        ) : (
          <SafeMarkdown markdown={markdown} />
        )}
      </CardContent>
    </Card>
  );
}

export function PlatformAnalyticsPage(): JSX.Element {
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const status = useQuery({
    queryKey: ["platform-analytics", "status"],
    queryFn: getAnalyticsChatStatus,
    staleTime: 5 * 60 * 1000,
  });

  const ask = useMutation({
    mutationFn: (q: string) => {
      // Only completed turns become history — a failed turn has no answer to
      // send back, and including the question alone would let the model
      // "remember" asking something it never answered.
      const history: AnalyticsChatMessage[] = turns
        .filter((t) => t.answer && !t.failed)
        .flatMap((t) => [
          { role: "user" as const, content: t.question },
          { role: "assistant" as const, content: t.answer as string },
        ]);
      return askAnalytics(q, history);
    },
    onSuccess: (turn) => {
      setTurns((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          ...next[next.length - 1],
          answer: turn.answer,
          steps: turn.steps,
        };
        return next;
      });
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    },
    onError: () => {
      setTurns((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], failed: true };
        return next;
      });
    },
  });

  const submit = (text: string): void => {
    const trimmed = text.trim();
    if (trimmed === "" || ask.isPending) return;
    setTurns((prev) => [...prev, { question: trimmed }]);
    setQuestion("");
    ask.mutate(trimmed);
  };

  if (status.data && !status.data.configured) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Card>
          <CardContent className="flex items-start gap-3 py-6">
            <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-[var(--fx-warn-text)]" />
            <div>
              <p className="font-semibold">Analytics chat is not configured here</p>
              <p className="mt-1 text-sm text-[var(--fx-ink-500)]">
                This environment is missing either <code>ANTHROPIC_API_KEY</code> or
                the read-only database credentials (<code>ANALYTICS_DB_USER</code> /
                <code> ANALYTICS_DB_PASSWORD</code>).
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] max-w-3xl flex-col px-4 py-6">
      <header className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Sparkles className="h-5 w-5 text-[var(--fx-accent-700)]" />
          Datos de producto
        </h1>
        <p className="mt-1 text-sm text-[var(--fx-ink-500)]">
          Preguntá sobre el uso del producto. Cada respuesta muestra las consultas
          que se ejecutaron.
        </p>
      </header>

      <div className="flex-1 space-y-6 overflow-y-auto pb-4">
        <DailyBrief />

        {turns.length === 0 && (
          <div className="space-y-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => submit(s)}
                className="block w-full rounded-[var(--fx-r-md)] border border-[var(--fx-ink-200)] px-3 py-2 text-left text-sm hover:bg-[var(--fx-s-raised-hover)]"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {turns.map((turn, i) => (
          <div key={i} className="space-y-2">
            <p className="font-medium">{turn.question}</p>
            {turn.answer !== undefined ? (
              <Card>
                <CardContent className="py-4 text-sm">
                  <SafeMarkdown markdown={turn.answer} />
                  <QueryDisclosure steps={turn.steps ?? []} />
                </CardContent>
              </Card>
            ) : turn.failed ? (
              <p className="text-sm text-[var(--fx-warn-text)]">
                No se pudo responder. Probá de nuevo.
              </p>
            ) : (
              <p className="flex items-center gap-2 text-sm text-[var(--fx-ink-500)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Consultando…
              </p>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(question);
        }}
        className="flex items-center gap-2 border-t border-[var(--fx-ink-200)] pt-3"
      >
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="¿Qué querés saber?"
          className="h-10 flex-1 rounded-[var(--fx-r-pill)] border border-[var(--fx-ink-200)] bg-[var(--fx-white)] px-4 text-sm outline-none focus:border-[var(--fx-accent-500)]"
        />
        <Button type="submit" size="icon" disabled={ask.isPending || question.trim() === ""}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
